/**
 * Agrupar planos da mesma pessoa na lista (spec 030, decisao 1 e 2).
 *
 * A lista de Inscricoes e de PLANOS, nao de pessoas: a pergunta do dia a dia e "que planos estao rodando". So que
 * quem tem dois planos aparece duas vezes e nada diz que e a mesma pessoa. No print que o Vitor mandou, duas das
 * quatro linhas eram do mesmo CPF, separadas por outras duas, e ainda por cima tres nomes comecavam com "Bruno":
 * quem bate o olho le tres pessoas parecidas em vez de duas, uma delas com dois planos.
 *
 * Medido em 23/09: 34 das 165 pessoas tem mais de um plano. E uma em cada cinco.
 *
 * O agrupamento e sempre por **CPF**, nunca por nome: nome parecido e coincidencia, CPF e identidade (spec 001).
 */

import { paginar, POR_PAGINA } from "@mtc/ui";

/** O minimo que uma linha precisa ter pra ser agrupada. A tela passa a inscricao inteira; o teste passa so isto. */
export type LinhaAgrupavel = { pessoa: { cpf: string } };

/** O que a tela precisa saber pra desenhar a linha. `posicao` e 1 pro primeiro plano da pessoa, 2 pro segundo... */
export type MarcaDoGrupo = { primeira: boolean; ultima: boolean; posicao: number; total: number };

const soDigitos = (s: string) => s.replace(/\D/g, "");

/**
 * Devolve as linhas reordenadas, com a marca do grupo de cada uma.
 *
 * Duas regras que a decisao 2 exige e que o teste cobre:
 * - as linhas da mesma pessoa ficam **juntas**, na posicao da MAIS RECENTE delas (ou seja, a primeira que aparece na
 *   lista ja ordenada que chega aqui); a ordem entre pessoas diferentes **nao muda**;
 * - quem tem um plano so nao ganha marca nenhuma (`total === 1`), e a tela desenha a linha exatamente como hoje.
 */
export function agruparPorCpf<T extends LinhaAgrupavel>(linhas: T[]): { linha: T; grupo: MarcaDoGrupo }[] {
  const porCpf = new Map<string, T[]>();
  const ordem: string[] = [];
  for (const linha of linhas) {
    const cpf = soDigitos(linha.pessoa.cpf);
    // CPF vazio ou ilegivel nao agrupa com ninguem: cada linha vira o proprio grupo, pra nunca juntar gente diferente.
    const chave = cpf || `sem-cpf:${ordem.length}`;
    const grupo = porCpf.get(chave);
    if (grupo) grupo.push(linha);
    else {
      porCpf.set(chave, [linha]);
      ordem.push(chave);
    }
  }
  return ordem.flatMap((chave) => {
    const grupo = porCpf.get(chave)!;
    return grupo.map((linha, i) => ({ linha, grupo: { primeira: i === 0, ultima: i === grupo.length - 1, posicao: i + 1, total: grupo.length } }));
  });
}

/**
 * Uma pagina da lista ja agrupada (spec 058). Quando a pagina comeca no meio de um cliente com varios planos, a primeira
 * linha dela vira a cabeca do grupo (com o nome e o CPF): sem isso a pagina abria com um "2º plano, mesmo cliente" sem
 * dizer de quem.
 */
export function paginaDosGrupos<T>(agrupadas: { linha: T; grupo: MarcaDoGrupo }[], pagina: number, porPagina = POR_PAGINA): { linha: T; grupo: MarcaDoGrupo }[] {
  return paginar(agrupadas, pagina, porPagina).map((a, i) => (i === 0 && !a.grupo.primeira ? { ...a, grupo: { ...a.grupo, primeira: true } } : a));
}
